import type { packsAsConst } from "@superego/boutique";
import type { TypeOf } from "@superego/schema";

type Food = TypeOf<(typeof packsAsConst)[1]["collections"][1]["schema"]>;

const kcal = (amount: number) => ({ unit: "kcal" as const, amount });
const g = (amount: number) => ({ unit: "g" as const, amount });
const mg = (amount: number) => ({ unit: "mg" as const, amount });

export default [
  // Fruits
  {
    name: "Apple",
    category: "Fruits",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(52),
      protein: g(0.3),
      totalCarbohydrate: g(14),
      dietaryFiber: g(2.4),
      totalSugars: g(10),
      totalFat: g(0.2),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.003),
      vitaminC: mg(4.6),
      vitaminD: null,
      vitaminE: mg(0.18),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.017),
      vitaminB2: mg(0.026),
      vitaminB3: mg(0.091),
      vitaminB6: mg(0.041),
      folate: mg(0.003),
      vitaminB12: null,
      calcium: mg(6),
      iron: mg(0.12),
      magnesium: mg(5),
      phosphorus: mg(11),
      potassium: mg(107),
      sodium: mg(1),
      zinc: mg(0.04),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Banana",
    category: "Fruits",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(89),
      protein: g(1.1),
      totalCarbohydrate: g(23),
      dietaryFiber: g(2.6),
      totalSugars: g(12),
      totalFat: g(0.3),
      saturatedFat: g(0.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.003),
      vitaminC: mg(8.7),
      vitaminD: null,
      vitaminE: mg(0.1),
      vitaminK: mg(0.0005),
      vitaminB1: mg(0.031),
      vitaminB2: mg(0.073),
      vitaminB3: mg(0.665),
      vitaminB6: mg(0.367),
      folate: mg(0.02),
      vitaminB12: null,
      calcium: mg(5),
      iron: mg(0.26),
      magnesium: mg(27),
      phosphorus: mg(22),
      potassium: mg(358),
      sodium: mg(1),
      zinc: mg(0.15),
      selenium: mg(0.001),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Orange",
    category: "Fruits",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(47),
      protein: g(0.9),
      totalCarbohydrate: g(12),
      dietaryFiber: g(2.4),
      totalSugars: g(9),
      totalFat: g(0.1),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.011),
      vitaminC: mg(53),
      vitaminD: null,
      vitaminE: mg(0.18),
      vitaminK: mg(0),
      vitaminB1: mg(0.087),
      vitaminB2: mg(0.04),
      vitaminB3: mg(0.282),
      vitaminB6: mg(0.06),
      folate: mg(0.03),
      vitaminB12: null,
      calcium: mg(40),
      iron: mg(0.1),
      magnesium: mg(10),
      phosphorus: mg(14),
      potassium: mg(181),
      sodium: mg(0),
      zinc: mg(0.07),
      selenium: mg(0.0005),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Strawberries",
    category: "Fruits",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(32),
      protein: g(0.7),
      totalCarbohydrate: g(7.7),
      dietaryFiber: g(2),
      totalSugars: g(4.9),
      totalFat: g(0.3),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.001),
      vitaminC: mg(59),
      vitaminD: null,
      vitaminE: mg(0.29),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.024),
      vitaminB2: mg(0.022),
      vitaminB3: mg(0.386),
      vitaminB6: mg(0.047),
      folate: mg(0.024),
      vitaminB12: null,
      calcium: mg(16),
      iron: mg(0.41),
      magnesium: mg(13),
      phosphorus: mg(24),
      potassium: mg(153),
      sodium: mg(1),
      zinc: mg(0.14),
      selenium: mg(0.0004),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Grapes",
    category: "Fruits",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(69),
      protein: g(0.7),
      totalCarbohydrate: g(18),
      dietaryFiber: g(0.9),
      totalSugars: g(16),
      totalFat: g(0.2),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.003),
      vitaminC: mg(3.2),
      vitaminD: null,
      vitaminE: mg(0.19),
      vitaminK: mg(0.015),
      vitaminB1: mg(0.069),
      vitaminB2: mg(0.07),
      vitaminB3: mg(0.188),
      vitaminB6: mg(0.086),
      folate: mg(0.002),
      vitaminB12: null,
      calcium: mg(10),
      iron: mg(0.36),
      magnesium: mg(7),
      phosphorus: mg(20),
      potassium: mg(191),
      sodium: mg(2),
      zinc: mg(0.07),
      selenium: mg(0.0001),
      iodine: null,
    },
    pictures: [],
  },

  // Vegetables
  {
    name: "Broccoli",
    category: "Vegetables",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(34),
      protein: g(2.8),
      totalCarbohydrate: g(7),
      dietaryFiber: g(2.6),
      totalSugars: g(1.7),
      totalFat: g(0.4),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.031),
      vitaminC: mg(89),
      vitaminD: null,
      vitaminE: mg(0.78),
      vitaminK: mg(0.102),
      vitaminB1: mg(0.071),
      vitaminB2: mg(0.117),
      vitaminB3: mg(0.639),
      vitaminB6: mg(0.175),
      folate: mg(0.063),
      vitaminB12: null,
      calcium: mg(47),
      iron: mg(0.73),
      magnesium: mg(21),
      phosphorus: mg(66),
      potassium: mg(316),
      sodium: mg(33),
      zinc: mg(0.41),
      selenium: mg(0.003),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Carrot",
    category: "Vegetables",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(41),
      protein: g(0.9),
      totalCarbohydrate: g(10),
      dietaryFiber: g(2.8),
      totalSugars: g(4.7),
      totalFat: g(0.2),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.835),
      vitaminC: mg(5.9),
      vitaminD: null,
      vitaminE: mg(0.66),
      vitaminK: mg(0.013),
      vitaminB1: mg(0.066),
      vitaminB2: mg(0.058),
      vitaminB3: mg(0.983),
      vitaminB6: mg(0.138),
      folate: mg(0.019),
      vitaminB12: null,
      calcium: mg(33),
      iron: mg(0.3),
      magnesium: mg(12),
      phosphorus: mg(35),
      potassium: mg(320),
      sodium: mg(69),
      zinc: mg(0.24),
      selenium: mg(0.0001),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Spinach",
    category: "Vegetables",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(23),
      protein: g(2.9),
      totalCarbohydrate: g(3.6),
      dietaryFiber: g(2.2),
      totalSugars: g(0.4),
      totalFat: g(0.4),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.469),
      vitaminC: mg(28),
      vitaminD: null,
      vitaminE: mg(2.03),
      vitaminK: mg(0.483),
      vitaminB1: mg(0.078),
      vitaminB2: mg(0.189),
      vitaminB3: mg(0.724),
      vitaminB6: mg(0.195),
      folate: mg(0.194),
      vitaminB12: null,
      calcium: mg(99),
      iron: mg(2.71),
      magnesium: mg(79),
      phosphorus: mg(49),
      potassium: mg(558),
      sodium: mg(79),
      zinc: mg(0.53),
      selenium: mg(0.001),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Tomato",
    category: "Vegetables",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(18),
      protein: g(0.9),
      totalCarbohydrate: g(3.9),
      dietaryFiber: g(1.2),
      totalSugars: g(2.6),
      totalFat: g(0.2),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.042),
      vitaminC: mg(14),
      vitaminD: null,
      vitaminE: mg(0.54),
      vitaminK: mg(0.008),
      vitaminB1: mg(0.037),
      vitaminB2: mg(0.019),
      vitaminB3: mg(0.594),
      vitaminB6: mg(0.08),
      folate: mg(0.015),
      vitaminB12: null,
      calcium: mg(10),
      iron: mg(0.27),
      magnesium: mg(11),
      phosphorus: mg(24),
      potassium: mg(237),
      sodium: mg(5),
      zinc: mg(0.17),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Potato",
    category: "Vegetables",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(77),
      protein: g(2),
      totalCarbohydrate: g(17),
      dietaryFiber: g(2.2),
      totalSugars: g(0.8),
      totalFat: g(0.1),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(20),
      vitaminD: null,
      vitaminE: mg(0.01),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.081),
      vitaminB2: mg(0.032),
      vitaminB3: mg(1.061),
      vitaminB6: mg(0.298),
      folate: mg(0.015),
      vitaminB12: null,
      calcium: mg(12),
      iron: mg(0.81),
      magnesium: mg(23),
      phosphorus: mg(57),
      potassium: mg(425),
      sodium: mg(6),
      zinc: mg(0.3),
      selenium: mg(0.0003),
      iodine: null,
    },
    pictures: [],
  },

  // Dairy & Eggs
  {
    name: "Whole Milk",
    category: "DairyAndEggs",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(61),
      protein: g(3.2),
      totalCarbohydrate: g(4.8),
      dietaryFiber: g(0),
      totalSugars: g(5),
      totalFat: g(3.3),
      saturatedFat: g(1.9),
      cholesterol: mg(10),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.046),
      vitaminC: mg(0),
      vitaminD: mg(0.001),
      vitaminE: mg(0.07),
      vitaminK: mg(0.0003),
      vitaminB1: mg(0.046),
      vitaminB2: mg(0.169),
      vitaminB3: mg(0.089),
      vitaminB6: mg(0.036),
      folate: mg(0.005),
      vitaminB12: mg(0.00045),
      calcium: mg(113),
      iron: mg(0.03),
      magnesium: mg(10),
      phosphorus: mg(84),
      potassium: mg(132),
      sodium: mg(43),
      zinc: mg(0.37),
      selenium: mg(0.002),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Greek Yogurt",
    category: "DairyAndEggs",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(59),
      protein: g(10),
      totalCarbohydrate: g(3.6),
      dietaryFiber: g(0),
      totalSugars: g(3.2),
      totalFat: g(0.7),
      saturatedFat: g(0.3),
      cholesterol: mg(5),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.002),
      vitaminC: mg(0),
      vitaminD: mg(0),
      vitaminE: mg(0.01),
      vitaminK: mg(0),
      vitaminB1: mg(0.023),
      vitaminB2: mg(0.278),
      vitaminB3: mg(0.208),
      vitaminB6: mg(0.063),
      folate: mg(0.007),
      vitaminB12: mg(0.00075),
      calcium: mg(110),
      iron: mg(0.07),
      magnesium: mg(11),
      phosphorus: mg(135),
      potassium: mg(141),
      sodium: mg(36),
      zinc: mg(0.52),
      selenium: mg(0.009),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Cheddar Cheese",
    category: "DairyAndEggs",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(403),
      protein: g(25),
      totalCarbohydrate: g(1.3),
      dietaryFiber: g(0),
      totalSugars: g(0.5),
      totalFat: g(33),
      saturatedFat: g(21),
      cholesterol: mg(105),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.265),
      vitaminC: mg(0),
      vitaminD: mg(0.0006),
      vitaminE: mg(0.29),
      vitaminK: mg(0.003),
      vitaminB1: mg(0.027),
      vitaminB2: mg(0.375),
      vitaminB3: mg(0.08),
      vitaminB6: mg(0.074),
      folate: mg(0.018),
      vitaminB12: mg(0.00083),
      calcium: mg(721),
      iron: mg(0.68),
      magnesium: mg(28),
      phosphorus: mg(512),
      potassium: mg(98),
      sodium: mg(621),
      zinc: mg(3.11),
      selenium: mg(0.014),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Egg (whole)",
    category: "DairyAndEggs",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(155),
      protein: g(13),
      totalCarbohydrate: g(1.1),
      dietaryFiber: g(0),
      totalSugars: g(1.1),
      totalFat: g(11),
      saturatedFat: g(3.3),
      cholesterol: mg(373),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.149),
      vitaminC: mg(0),
      vitaminD: mg(0.002),
      vitaminE: mg(1.05),
      vitaminK: mg(0.0003),
      vitaminB1: mg(0.04),
      vitaminB2: mg(0.457),
      vitaminB3: mg(0.075),
      vitaminB6: mg(0.17),
      folate: mg(0.047),
      vitaminB12: mg(0.00089),
      calcium: mg(56),
      iron: mg(1.75),
      magnesium: mg(12),
      phosphorus: mg(198),
      potassium: mg(138),
      sodium: mg(142),
      zinc: mg(1.29),
      selenium: mg(0.031),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Butter",
    category: "DairyAndEggs",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(717),
      protein: g(0.9),
      totalCarbohydrate: g(0.1),
      dietaryFiber: g(0),
      totalSugars: g(0.1),
      totalFat: g(81),
      saturatedFat: g(51),
      cholesterol: mg(215),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.684),
      vitaminC: mg(0),
      vitaminD: mg(0.0015),
      vitaminE: mg(2.32),
      vitaminK: mg(0.007),
      vitaminB1: mg(0.005),
      vitaminB2: mg(0.034),
      vitaminB3: mg(0.042),
      vitaminB6: mg(0.003),
      folate: mg(0.003),
      vitaminB12: mg(0.00017),
      calcium: mg(24),
      iron: mg(0.02),
      magnesium: mg(2),
      phosphorus: mg(24),
      potassium: mg(24),
      sodium: mg(643),
      zinc: mg(0.09),
      selenium: mg(0.001),
      iodine: null,
    },
    pictures: [],
  },

  // Meat & Poultry
  {
    name: "Chicken Breast",
    category: "MeatAndPoultry",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(165),
      protein: g(31),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(3.6),
      saturatedFat: g(1),
      cholesterol: mg(85),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.006),
      vitaminC: mg(0),
      vitaminD: mg(0.0001),
      vitaminE: mg(0.27),
      vitaminK: mg(0),
      vitaminB1: mg(0.063),
      vitaminB2: mg(0.085),
      vitaminB3: mg(13.7),
      vitaminB6: mg(0.6),
      folate: mg(0.004),
      vitaminB12: mg(0.00034),
      calcium: mg(11),
      iron: mg(0.37),
      magnesium: mg(29),
      phosphorus: mg(228),
      potassium: mg(256),
      sodium: mg(74),
      zinc: mg(0.68),
      selenium: mg(0.024),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Ground Beef (80% lean)",
    category: "MeatAndPoultry",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(254),
      protein: g(26),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(17),
      saturatedFat: g(6.8),
      cholesterol: mg(78),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: mg(0.0001),
      vitaminE: mg(0.12),
      vitaminK: mg(0.001),
      vitaminB1: mg(0.046),
      vitaminB2: mg(0.181),
      vitaminB3: mg(5.378),
      vitaminB6: mg(0.383),
      folate: mg(0.006),
      vitaminB12: mg(0.00261),
      calcium: mg(18),
      iron: mg(2.6),
      magnesium: mg(21),
      phosphorus: mg(201),
      potassium: mg(318),
      sodium: mg(75),
      zinc: mg(5.41),
      selenium: mg(0.018),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Pork Chop",
    category: "MeatAndPoultry",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(231),
      protein: g(25),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(14),
      saturatedFat: g(5),
      cholesterol: mg(72),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.002),
      vitaminC: mg(0),
      vitaminD: mg(0.0005),
      vitaminE: mg(0.21),
      vitaminK: mg(0),
      vitaminB1: mg(0.877),
      vitaminB2: mg(0.256),
      vitaminB3: mg(4.8),
      vitaminB6: mg(0.464),
      folate: mg(0.001),
      vitaminB12: mg(0.00054),
      calcium: mg(19),
      iron: mg(0.87),
      magnesium: mg(28),
      phosphorus: mg(246),
      potassium: mg(423),
      sodium: mg(62),
      zinc: mg(2.39),
      selenium: mg(0.033),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Turkey Breast",
    category: "MeatAndPoultry",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(135),
      protein: g(30),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(0.7),
      saturatedFat: g(0.2),
      cholesterol: mg(83),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: mg(0.0001),
      vitaminE: mg(0.06),
      vitaminK: mg(0),
      vitaminB1: mg(0.048),
      vitaminB2: mg(0.167),
      vitaminB3: mg(11.75),
      vitaminB6: mg(0.81),
      folate: mg(0.006),
      vitaminB12: mg(0.00036),
      calcium: mg(8),
      iron: mg(0.54),
      magnesium: mg(32),
      phosphorus: mg(230),
      potassium: mg(293),
      sodium: mg(46),
      zinc: mg(1.24),
      selenium: mg(0.031),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Bacon",
    category: "MeatAndPoultry",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(541),
      protein: g(37),
      totalCarbohydrate: g(1.4),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(42),
      saturatedFat: g(14),
      cholesterol: mg(110),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.011),
      vitaminC: mg(0),
      vitaminD: mg(0.0005),
      vitaminE: mg(0.43),
      vitaminK: mg(0),
      vitaminB1: mg(0.44),
      vitaminB2: mg(0.19),
      vitaminB3: mg(11.1),
      vitaminB6: mg(0.4),
      folate: mg(0.002),
      vitaminB12: mg(0.001),
      calcium: mg(11),
      iron: mg(1.44),
      magnesium: mg(33),
      phosphorus: mg(533),
      potassium: mg(565),
      sodium: mg(1717),
      zinc: mg(3.22),
      selenium: mg(0.046),
      iodine: null,
    },
    pictures: [],
  },

  // Fish & Seafood
  {
    name: "Salmon",
    category: "FishAndSeafood",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(208),
      protein: g(20),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(13),
      saturatedFat: g(3.1),
      cholesterol: mg(55),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.012),
      vitaminC: mg(0),
      vitaminD: mg(0.011),
      vitaminE: mg(3.55),
      vitaminK: mg(0.0001),
      vitaminB1: mg(0.226),
      vitaminB2: mg(0.38),
      vitaminB3: mg(8.0),
      vitaminB6: mg(0.64),
      folate: mg(0.025),
      vitaminB12: mg(0.00318),
      calcium: mg(12),
      iron: mg(0.34),
      magnesium: mg(27),
      phosphorus: mg(200),
      potassium: mg(363),
      sodium: mg(59),
      zinc: mg(0.36),
      selenium: mg(0.037),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Tuna (canned)",
    category: "FishAndSeafood",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(116),
      protein: g(26),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(0.8),
      saturatedFat: g(0.2),
      cholesterol: mg(30),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.018),
      vitaminC: mg(0),
      vitaminD: mg(0.002),
      vitaminE: mg(0.85),
      vitaminK: mg(0),
      vitaminB1: mg(0.017),
      vitaminB2: mg(0.115),
      vitaminB3: mg(10.5),
      vitaminB6: mg(0.455),
      folate: mg(0.002),
      vitaminB12: mg(0.00291),
      calcium: mg(11),
      iron: mg(0.97),
      magnesium: mg(30),
      phosphorus: mg(311),
      potassium: mg(252),
      sodium: mg(338),
      zinc: mg(0.77),
      selenium: mg(0.071),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Shrimp",
    category: "FishAndSeafood",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(99),
      protein: g(24),
      totalCarbohydrate: g(0.2),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(0.3),
      saturatedFat: g(0.1),
      cholesterol: mg(189),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.054),
      vitaminC: mg(0),
      vitaminD: mg(0),
      vitaminE: mg(1.32),
      vitaminK: mg(0),
      vitaminB1: mg(0.028),
      vitaminB2: mg(0.034),
      vitaminB3: mg(2.6),
      vitaminB6: mg(0.103),
      folate: mg(0.019),
      vitaminB12: mg(0.00148),
      calcium: mg(70),
      iron: mg(0.52),
      magnesium: mg(39),
      phosphorus: mg(237),
      potassium: mg(259),
      sodium: mg(111),
      zinc: mg(1.64),
      selenium: mg(0.04),
      iodine: null,
    },
    pictures: [],
  },

  // Grains & Starches
  {
    name: "White Rice (cooked)",
    category: "GrainsAndStarches",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(130),
      protein: g(2.7),
      totalCarbohydrate: g(28),
      dietaryFiber: g(0.4),
      totalSugars: g(0),
      totalFat: g(0.3),
      saturatedFat: g(0.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.04),
      vitaminK: mg(0),
      vitaminB1: mg(0.02),
      vitaminB2: mg(0.013),
      vitaminB3: mg(0.4),
      vitaminB6: mg(0.093),
      folate: mg(0.003),
      vitaminB12: null,
      calcium: mg(10),
      iron: mg(0.2),
      magnesium: mg(12),
      phosphorus: mg(43),
      potassium: mg(35),
      sodium: mg(1),
      zinc: mg(0.49),
      selenium: mg(0.008),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Brown Rice (cooked)",
    category: "GrainsAndStarches",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(111),
      protein: g(2.6),
      totalCarbohydrate: g(23),
      dietaryFiber: g(1.8),
      totalSugars: g(0.4),
      totalFat: g(0.9),
      saturatedFat: g(0.2),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.04),
      vitaminK: mg(0.001),
      vitaminB1: mg(0.101),
      vitaminB2: mg(0.025),
      vitaminB3: mg(1.53),
      vitaminB6: mg(0.149),
      folate: mg(0.004),
      vitaminB12: null,
      calcium: mg(10),
      iron: mg(0.42),
      magnesium: mg(43),
      phosphorus: mg(83),
      potassium: mg(43),
      sodium: mg(5),
      zinc: mg(0.63),
      selenium: mg(0.01),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Pasta (cooked)",
    category: "GrainsAndStarches",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(131),
      protein: g(5),
      totalCarbohydrate: g(25),
      dietaryFiber: g(1.8),
      totalSugars: g(0.6),
      totalFat: g(1.1),
      saturatedFat: g(0.2),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.06),
      vitaminK: mg(0),
      vitaminB1: mg(0.02),
      vitaminB2: mg(0.02),
      vitaminB3: mg(0.4),
      vitaminB6: mg(0.049),
      folate: mg(0.007),
      vitaminB12: null,
      calcium: mg(7),
      iron: mg(0.5),
      magnesium: mg(18),
      phosphorus: mg(58),
      potassium: mg(44),
      sodium: mg(1),
      zinc: mg(0.51),
      selenium: mg(0.026),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Whole Wheat Bread",
    category: "GrainsAndStarches",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(247),
      protein: g(13),
      totalCarbohydrate: g(41),
      dietaryFiber: g(7),
      totalSugars: g(6),
      totalFat: g(3.4),
      saturatedFat: g(0.7),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.37),
      vitaminK: mg(0.007),
      vitaminB1: mg(0.39),
      vitaminB2: mg(0.166),
      vitaminB3: mg(4.43),
      vitaminB6: mg(0.215),
      folate: mg(0.042),
      vitaminB12: null,
      calcium: mg(107),
      iron: mg(2.43),
      magnesium: mg(75),
      phosphorus: mg(212),
      potassium: mg(254),
      sodium: mg(400),
      zinc: mg(1.77),
      selenium: mg(0.036),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Oatmeal (cooked)",
    category: "GrainsAndStarches",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(71),
      protein: g(2.5),
      totalCarbohydrate: g(12),
      dietaryFiber: g(1.7),
      totalSugars: g(0.5),
      totalFat: g(1.5),
      saturatedFat: g(0.3),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.08),
      vitaminK: mg(0.001),
      vitaminB1: mg(0.08),
      vitaminB2: mg(0.022),
      vitaminB3: mg(0.23),
      vitaminB6: mg(0.025),
      folate: mg(0.006),
      vitaminB12: null,
      calcium: mg(9),
      iron: mg(0.9),
      magnesium: mg(27),
      phosphorus: mg(77),
      potassium: mg(61),
      sodium: mg(4),
      zinc: mg(0.62),
      selenium: mg(0.005),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Quinoa (cooked)",
    category: "GrainsAndStarches",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(120),
      protein: g(4.4),
      totalCarbohydrate: g(21),
      dietaryFiber: g(2.8),
      totalSugars: g(0.9),
      totalFat: g(1.9),
      saturatedFat: g(0.2),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.001),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.63),
      vitaminK: mg(0),
      vitaminB1: mg(0.107),
      vitaminB2: mg(0.11),
      vitaminB3: mg(0.412),
      vitaminB6: mg(0.123),
      folate: mg(0.042),
      vitaminB12: null,
      calcium: mg(17),
      iron: mg(1.49),
      magnesium: mg(64),
      phosphorus: mg(152),
      potassium: mg(172),
      sodium: mg(7),
      zinc: mg(1.09),
      selenium: mg(0.003),
      iodine: null,
    },
    pictures: [],
  },

  // Plant Proteins
  {
    name: "Tofu",
    category: "PlantProteins",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(76),
      protein: g(8),
      totalCarbohydrate: g(1.9),
      dietaryFiber: g(0.3),
      totalSugars: g(0.6),
      totalFat: g(4.8),
      saturatedFat: g(0.7),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.01),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.081),
      vitaminB2: mg(0.052),
      vitaminB3: mg(0.195),
      vitaminB6: mg(0.047),
      folate: mg(0.015),
      vitaminB12: null,
      calcium: mg(350),
      iron: mg(5.36),
      magnesium: mg(30),
      phosphorus: mg(97),
      potassium: mg(121),
      sodium: mg(7),
      zinc: mg(0.8),
      selenium: mg(0.009),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Black Beans (cooked)",
    category: "PlantProteins",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(132),
      protein: g(8.9),
      totalCarbohydrate: g(24),
      dietaryFiber: g(8.7),
      totalSugars: g(0.3),
      totalFat: g(0.5),
      saturatedFat: g(0.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.09),
      vitaminK: mg(0.003),
      vitaminB1: mg(0.244),
      vitaminB2: mg(0.059),
      vitaminB3: mg(0.505),
      vitaminB6: mg(0.069),
      folate: mg(0.149),
      vitaminB12: null,
      calcium: mg(27),
      iron: mg(2.1),
      magnesium: mg(70),
      phosphorus: mg(140),
      potassium: mg(355),
      sodium: mg(1),
      zinc: mg(1.12),
      selenium: mg(0.001),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Lentils (cooked)",
    category: "PlantProteins",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(116),
      protein: g(9),
      totalCarbohydrate: g(20),
      dietaryFiber: g(7.9),
      totalSugars: g(1.8),
      totalFat: g(0.4),
      saturatedFat: g(0.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.001),
      vitaminC: mg(1.5),
      vitaminD: null,
      vitaminE: mg(0.11),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.169),
      vitaminB2: mg(0.073),
      vitaminB3: mg(1.06),
      vitaminB6: mg(0.178),
      folate: mg(0.181),
      vitaminB12: null,
      calcium: mg(19),
      iron: mg(3.33),
      magnesium: mg(36),
      phosphorus: mg(180),
      potassium: mg(369),
      sodium: mg(2),
      zinc: mg(1.27),
      selenium: mg(0.003),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Chickpeas (cooked)",
    category: "PlantProteins",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(164),
      protein: g(8.9),
      totalCarbohydrate: g(27),
      dietaryFiber: g(7.6),
      totalSugars: g(4.8),
      totalFat: g(2.6),
      saturatedFat: g(0.3),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.001),
      vitaminC: mg(1.3),
      vitaminD: null,
      vitaminE: mg(0.35),
      vitaminK: mg(0.004),
      vitaminB1: mg(0.116),
      vitaminB2: mg(0.063),
      vitaminB3: mg(0.526),
      vitaminB6: mg(0.139),
      folate: mg(0.172),
      vitaminB12: null,
      calcium: mg(49),
      iron: mg(2.89),
      magnesium: mg(48),
      phosphorus: mg(168),
      potassium: mg(291),
      sodium: mg(7),
      zinc: mg(1.53),
      selenium: mg(0.004),
      iodine: null,
    },
    pictures: [],
  },

  // Nuts, Seeds & Dried Fruit
  {
    name: "Almonds",
    category: "NutsSeedsAndDriedFruit",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(579),
      protein: g(21),
      totalCarbohydrate: g(22),
      dietaryFiber: g(12),
      totalSugars: g(4.4),
      totalFat: g(50),
      saturatedFat: g(3.8),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(25.6),
      vitaminK: mg(0),
      vitaminB1: mg(0.205),
      vitaminB2: mg(1.138),
      vitaminB3: mg(3.618),
      vitaminB6: mg(0.137),
      folate: mg(0.044),
      vitaminB12: null,
      calcium: mg(269),
      iron: mg(3.71),
      magnesium: mg(270),
      phosphorus: mg(481),
      potassium: mg(733),
      sodium: mg(1),
      zinc: mg(3.12),
      selenium: mg(0.004),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Walnuts",
    category: "NutsSeedsAndDriedFruit",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(654),
      protein: g(15),
      totalCarbohydrate: g(14),
      dietaryFiber: g(6.7),
      totalSugars: g(2.6),
      totalFat: g(65),
      saturatedFat: g(6.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.001),
      vitaminC: mg(1.3),
      vitaminD: null,
      vitaminE: mg(0.7),
      vitaminK: mg(0.003),
      vitaminB1: mg(0.341),
      vitaminB2: mg(0.15),
      vitaminB3: mg(1.125),
      vitaminB6: mg(0.537),
      folate: mg(0.098),
      vitaminB12: null,
      calcium: mg(98),
      iron: mg(2.91),
      magnesium: mg(158),
      phosphorus: mg(346),
      potassium: mg(441),
      sodium: mg(2),
      zinc: mg(3.09),
      selenium: mg(0.005),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Peanuts",
    category: "NutsSeedsAndDriedFruit",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(567),
      protein: g(26),
      totalCarbohydrate: g(16),
      dietaryFiber: g(8.5),
      totalSugars: g(4),
      totalFat: g(49),
      saturatedFat: g(6.8),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(8.33),
      vitaminK: mg(0),
      vitaminB1: mg(0.64),
      vitaminB2: mg(0.135),
      vitaminB3: mg(12.07),
      vitaminB6: mg(0.348),
      folate: mg(0.24),
      vitaminB12: null,
      calcium: mg(92),
      iron: mg(4.58),
      magnesium: mg(168),
      phosphorus: mg(376),
      potassium: mg(705),
      sodium: mg(18),
      zinc: mg(3.27),
      selenium: mg(0.007),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Chia Seeds",
    category: "NutsSeedsAndDriedFruit",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(486),
      protein: g(17),
      totalCarbohydrate: g(42),
      dietaryFiber: g(34),
      totalSugars: g(0),
      totalFat: g(31),
      saturatedFat: g(3.3),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.054),
      vitaminC: mg(1.6),
      vitaminD: null,
      vitaminE: mg(0.5),
      vitaminK: mg(0),
      vitaminB1: mg(0.62),
      vitaminB2: mg(0.17),
      vitaminB3: mg(8.83),
      vitaminB6: mg(0),
      folate: mg(0.049),
      vitaminB12: null,
      calcium: mg(631),
      iron: mg(7.72),
      magnesium: mg(335),
      phosphorus: mg(860),
      potassium: mg(407),
      sodium: mg(16),
      zinc: mg(4.58),
      selenium: mg(0.055),
      iodine: null,
    },
    pictures: [],
  },

  // Beverages
  {
    name: "Orange Juice",
    category: "Beverages",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(45),
      protein: g(0.7),
      totalCarbohydrate: g(10),
      dietaryFiber: g(0.2),
      totalSugars: g(8.4),
      totalFat: g(0.2),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.01),
      vitaminC: mg(50),
      vitaminD: null,
      vitaminE: mg(0.04),
      vitaminK: mg(0),
      vitaminB1: mg(0.09),
      vitaminB2: mg(0.03),
      vitaminB3: mg(0.4),
      vitaminB6: mg(0.04),
      folate: mg(0.03),
      vitaminB12: null,
      calcium: mg(11),
      iron: mg(0.2),
      magnesium: mg(11),
      phosphorus: mg(17),
      potassium: mg(200),
      sodium: mg(1),
      zinc: mg(0.05),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Coffee (black)",
    category: "Beverages",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(2),
      protein: g(0.1),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(0),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: mg(40),
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0),
      vitaminK: mg(0),
      vitaminB1: mg(0.014),
      vitaminB2: mg(0.076),
      vitaminB3: mg(0.191),
      vitaminB6: mg(0.001),
      folate: mg(0.002),
      vitaminB12: null,
      calcium: mg(2),
      iron: mg(0.01),
      magnesium: mg(3),
      phosphorus: mg(3),
      potassium: mg(49),
      sodium: mg(2),
      zinc: mg(0.02),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Green Tea",
    category: "Beverages",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(1),
      protein: g(0),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(0),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: mg(12),
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0),
      vitaminK: mg(0),
      vitaminB1: mg(0.007),
      vitaminB2: mg(0.06),
      vitaminB3: mg(0.03),
      vitaminB6: mg(0.005),
      folate: mg(0.012),
      vitaminB12: null,
      calcium: mg(0),
      iron: mg(0.02),
      magnesium: mg(0),
      phosphorus: mg(1),
      potassium: mg(8),
      sodium: mg(1),
      zinc: mg(0.01),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },

  // Fats & Oils
  {
    name: "Olive Oil",
    category: "FatsAndOils",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(884),
      protein: g(0),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(100),
      saturatedFat: g(14),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(14.4),
      vitaminK: mg(0.06),
      vitaminB1: mg(0),
      vitaminB2: mg(0),
      vitaminB3: mg(0),
      vitaminB6: mg(0),
      folate: mg(0),
      vitaminB12: null,
      calcium: mg(1),
      iron: mg(0.56),
      magnesium: mg(0),
      phosphorus: mg(0),
      potassium: mg(1),
      sodium: mg(2),
      zinc: mg(0),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Coconut Oil",
    category: "FatsAndOils",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(862),
      protein: g(0),
      totalCarbohydrate: g(0),
      dietaryFiber: g(0),
      totalSugars: g(0),
      totalFat: g(100),
      saturatedFat: g(82),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.09),
      vitaminK: mg(0.001),
      vitaminB1: mg(0),
      vitaminB2: mg(0),
      vitaminB3: mg(0),
      vitaminB6: mg(0),
      folate: mg(0),
      vitaminB12: null,
      calcium: mg(0),
      iron: mg(0.04),
      magnesium: mg(0),
      phosphorus: mg(0),
      potassium: mg(0),
      sodium: mg(0),
      zinc: mg(0),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },

  // Condiments, Sauces & Spreads
  {
    name: "Peanut Butter",
    category: "CondimentsSaucesAndSpreads",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(588),
      protein: g(25),
      totalCarbohydrate: g(20),
      dietaryFiber: g(6),
      totalSugars: g(9.2),
      totalFat: g(50),
      saturatedFat: g(10),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(9.1),
      vitaminK: mg(0),
      vitaminB1: mg(0.155),
      vitaminB2: mg(0.192),
      vitaminB3: mg(13.4),
      vitaminB6: mg(0.441),
      folate: mg(0.074),
      vitaminB12: null,
      calcium: mg(43),
      iron: mg(1.9),
      magnesium: mg(154),
      phosphorus: mg(335),
      potassium: mg(649),
      sodium: mg(426),
      zinc: mg(2.78),
      selenium: mg(0.004),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Honey",
    category: "Sweeteners",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(304),
      protein: g(0.3),
      totalCarbohydrate: g(82),
      dietaryFiber: g(0.2),
      totalSugars: g(82),
      totalFat: g(0),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(0.5),
      vitaminD: null,
      vitaminE: mg(0),
      vitaminK: mg(0),
      vitaminB1: mg(0),
      vitaminB2: mg(0.038),
      vitaminB3: mg(0.121),
      vitaminB6: mg(0.024),
      folate: mg(0.002),
      vitaminB12: null,
      calcium: mg(6),
      iron: mg(0.42),
      magnesium: mg(2),
      phosphorus: mg(4),
      potassium: mg(52),
      sodium: mg(4),
      zinc: mg(0.22),
      selenium: mg(0.001),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Ketchup",
    category: "CondimentsSaucesAndSpreads",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(112),
      protein: g(1.7),
      totalCarbohydrate: g(26),
      dietaryFiber: g(0.3),
      totalSugars: g(22),
      totalFat: g(0.1),
      saturatedFat: g(0),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.026),
      vitaminC: mg(4),
      vitaminD: null,
      vitaminE: mg(1.46),
      vitaminK: mg(0.003),
      vitaminB1: mg(0.02),
      vitaminB2: mg(0.166),
      vitaminB3: mg(1.43),
      vitaminB6: mg(0.16),
      folate: mg(0.009),
      vitaminB12: null,
      calcium: mg(18),
      iron: mg(0.43),
      magnesium: mg(19),
      phosphorus: mg(32),
      potassium: mg(315),
      sodium: mg(907),
      zinc: mg(0.24),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Mayonnaise",
    category: "CondimentsSaucesAndSpreads",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(680),
      protein: g(1),
      totalCarbohydrate: g(0.6),
      dietaryFiber: g(0),
      totalSugars: g(0.6),
      totalFat: g(75),
      saturatedFat: g(12),
      cholesterol: mg(42),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.008),
      vitaminC: mg(0),
      vitaminD: mg(0),
      vitaminE: mg(3.3),
      vitaminK: mg(0.163),
      vitaminB1: mg(0.039),
      vitaminB2: mg(0.026),
      vitaminB3: mg(0.003),
      vitaminB6: mg(0.063),
      folate: mg(0.003),
      vitaminB12: mg(0.00026),
      calcium: mg(8),
      iron: mg(0.5),
      magnesium: mg(1),
      phosphorus: mg(27),
      potassium: mg(20),
      sodium: mg(635),
      zinc: mg(0.22),
      selenium: mg(0.003),
      iodine: null,
    },
    pictures: [],
  },

  // Bakery & Desserts
  {
    name: "Chocolate (dark)",
    category: "BakeryAndDesserts",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(598),
      protein: g(7.8),
      totalCarbohydrate: g(46),
      dietaryFiber: g(11),
      totalSugars: g(24),
      totalFat: g(43),
      saturatedFat: g(25),
      cholesterol: mg(8),
      caffeine: mg(80),
      alcohol: null,
      vitaminA: mg(0.002),
      vitaminC: mg(0),
      vitaminD: mg(0),
      vitaminE: mg(0.59),
      vitaminK: mg(0.007),
      vitaminB1: mg(0.034),
      vitaminB2: mg(0.078),
      vitaminB3: mg(1.054),
      vitaminB6: mg(0.038),
      folate: mg(0.012),
      vitaminB12: mg(0.00028),
      calcium: mg(73),
      iron: mg(11.9),
      magnesium: mg(228),
      phosphorus: mg(308),
      potassium: mg(715),
      sodium: mg(20),
      zinc: mg(3.31),
      selenium: mg(0.007),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Vanilla Ice Cream",
    category: "BakeryAndDesserts",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(207),
      protein: g(3.5),
      totalCarbohydrate: g(24),
      dietaryFiber: g(0),
      totalSugars: g(21),
      totalFat: g(11),
      saturatedFat: g(6.8),
      cholesterol: mg(44),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.118),
      vitaminC: mg(0.6),
      vitaminD: mg(0.0002),
      vitaminE: mg(0.3),
      vitaminK: mg(0.003),
      vitaminB1: mg(0.041),
      vitaminB2: mg(0.24),
      vitaminB3: mg(0.116),
      vitaminB6: mg(0.052),
      folate: mg(0.005),
      vitaminB12: mg(0.00039),
      calcium: mg(128),
      iron: mg(0.09),
      magnesium: mg(14),
      phosphorus: mg(105),
      potassium: mg(199),
      sodium: mg(80),
      zinc: mg(0.69),
      selenium: mg(0.002),
      iodine: null,
    },
    pictures: [],
  },

  // Snacks
  {
    name: "Potato Chips",
    category: "Snacks",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(536),
      protein: g(7),
      totalCarbohydrate: g(53),
      dietaryFiber: g(4.4),
      totalSugars: g(0.3),
      totalFat: g(35),
      saturatedFat: g(3.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(24),
      vitaminD: null,
      vitaminE: mg(6),
      vitaminK: mg(0.013),
      vitaminB1: mg(0.167),
      vitaminB2: mg(0.066),
      vitaminB3: mg(4.58),
      vitaminB6: mg(0.64),
      folate: mg(0.027),
      vitaminB12: null,
      calcium: mg(24),
      iron: mg(1.64),
      magnesium: mg(66),
      phosphorus: mg(167),
      potassium: mg(1275),
      sodium: mg(525),
      zinc: mg(1.09),
      selenium: mg(0.008),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Popcorn (air-popped)",
    category: "Snacks",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(387),
      protein: g(13),
      totalCarbohydrate: g(78),
      dietaryFiber: g(15),
      totalSugars: g(0.9),
      totalFat: g(4.5),
      saturatedFat: g(0.6),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.01),
      vitaminC: mg(0),
      vitaminD: null,
      vitaminE: mg(0.29),
      vitaminK: mg(0.001),
      vitaminB1: mg(0.104),
      vitaminB2: mg(0.083),
      vitaminB3: mg(2.308),
      vitaminB6: mg(0.157),
      folate: mg(0.031),
      vitaminB12: null,
      calcium: mg(7),
      iron: mg(3.19),
      magnesium: mg(144),
      phosphorus: mg(358),
      potassium: mg(329),
      sodium: mg(8),
      zinc: mg(3.08),
      selenium: mg(0),
      iodine: null,
    },
    pictures: [],
  },

  // Soups, Stews & Broths
  {
    name: "Chicken Soup",
    category: "SoupsStewsAndBroths",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(36),
      protein: g(2.5),
      totalCarbohydrate: g(3.2),
      dietaryFiber: g(0.4),
      totalSugars: g(0.5),
      totalFat: g(1.4),
      saturatedFat: g(0.4),
      cholesterol: mg(7),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.067),
      vitaminC: mg(0.5),
      vitaminD: mg(0),
      vitaminE: mg(0.1),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.015),
      vitaminB2: mg(0.03),
      vitaminB3: mg(1.1),
      vitaminB6: mg(0.05),
      folate: mg(0.003),
      vitaminB12: mg(0.0001),
      calcium: mg(8),
      iron: mg(0.4),
      magnesium: mg(5),
      phosphorus: mg(30),
      potassium: mg(55),
      sodium: mg(343),
      zinc: mg(0.24),
      selenium: mg(0.005),
      iodine: null,
    },
    pictures: [],
  },

  // Prepared/Ready Meals
  {
    name: "Frozen Pizza (cheese)",
    category: "PreparedReadyMeals",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(266),
      protein: g(11),
      totalCarbohydrate: g(33),
      dietaryFiber: g(2.3),
      totalSugars: g(3.6),
      totalFat: g(10),
      saturatedFat: g(4.5),
      cholesterol: mg(18),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.074),
      vitaminC: mg(1),
      vitaminD: mg(0.0001),
      vitaminE: mg(0.52),
      vitaminK: mg(0.006),
      vitaminB1: mg(0.329),
      vitaminB2: mg(0.248),
      vitaminB3: mg(3.327),
      vitaminB6: mg(0.121),
      folate: mg(0.095),
      vitaminB12: mg(0.00047),
      calcium: mg(201),
      iron: mg(2.43),
      magnesium: mg(23),
      phosphorus: mg(231),
      potassium: mg(172),
      sodium: mg(598),
      zinc: mg(1.34),
      selenium: mg(0.019),
      iodine: null,
    },
    pictures: [],
  },

  // Sports/Meal Replacement Products
  {
    name: "Whey Protein Powder",
    category: "SportsMealReplacementProducts",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(380),
      protein: g(78),
      totalCarbohydrate: g(8),
      dietaryFiber: g(0),
      totalSugars: g(5),
      totalFat: g(4),
      saturatedFat: g(2.5),
      cholesterol: mg(120),
      caffeine: null,
      alcohol: null,
      vitaminA: null,
      vitaminC: null,
      vitaminD: null,
      vitaminE: null,
      vitaminK: null,
      vitaminB1: null,
      vitaminB2: null,
      vitaminB3: null,
      vitaminB6: null,
      folate: null,
      vitaminB12: null,
      calcium: mg(600),
      iron: null,
      magnesium: null,
      phosphorus: mg(400),
      potassium: mg(500),
      sodium: mg(200),
      zinc: null,
      selenium: null,
      iodine: null,
    },
    pictures: [],
  },

  // Herbs, Spices & Seasonings
  {
    name: "Garlic (raw)",
    category: "HerbsSpicesAndSeasonings",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(149),
      protein: g(6.4),
      totalCarbohydrate: g(33),
      dietaryFiber: g(2.1),
      totalSugars: g(1),
      totalFat: g(0.5),
      saturatedFat: g(0.1),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0),
      vitaminC: mg(31),
      vitaminD: null,
      vitaminE: mg(0.08),
      vitaminK: mg(0.002),
      vitaminB1: mg(0.2),
      vitaminB2: mg(0.11),
      vitaminB3: mg(0.7),
      vitaminB6: mg(1.235),
      folate: mg(0.003),
      vitaminB12: null,
      calcium: mg(181),
      iron: mg(1.7),
      magnesium: mg(25),
      phosphorus: mg(153),
      potassium: mg(401),
      sodium: mg(17),
      zinc: mg(1.16),
      selenium: mg(0.014),
      iodine: null,
    },
    pictures: [],
  },
  {
    name: "Cinnamon (ground)",
    category: "HerbsSpicesAndSeasonings",
    servingSize: g(100),
    foodFacts: {
      calories: kcal(247),
      protein: g(4),
      totalCarbohydrate: g(81),
      dietaryFiber: g(53),
      totalSugars: g(2.2),
      totalFat: g(1.2),
      saturatedFat: g(0.3),
      cholesterol: mg(0),
      caffeine: null,
      alcohol: null,
      vitaminA: mg(0.015),
      vitaminC: mg(3.8),
      vitaminD: null,
      vitaminE: mg(2.32),
      vitaminK: mg(0.031),
      vitaminB1: mg(0.022),
      vitaminB2: mg(0.041),
      vitaminB3: mg(1.332),
      vitaminB6: mg(0.158),
      folate: mg(0.006),
      vitaminB12: null,
      calcium: mg(1002),
      iron: mg(8.32),
      magnesium: mg(60),
      phosphorus: mg(64),
      potassium: mg(431),
      sodium: mg(10),
      zinc: mg(1.83),
      selenium: mg(0.003),
      iodine: null,
    },
    pictures: [],
  },
] satisfies Food[];
